import { useEffect, useState, useMemo } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Search, CheckCircle2, XCircle, LinkIcon, Sparkles } from "lucide-react";

const TYPE_META = {
  approved:            { label: "Aprobación",       Icon: CheckCircle2, style: "bg-primary/15 text-primary" },
  rejected:            { label: "Rechazo",          Icon: XCircle,      style: "bg-destructive/15 text-destructive" },
  invitation_created:  { label: "Invitación creada", Icon: Sparkles,    style: "bg-accent/15 text-accent" },
  invitation_used:     { label: "Registro por invitación", Icon: LinkIcon, style: "bg-warning/20 text-foreground" },
};

export default function AdminAudit() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");

  useEffect(() => {
    api.get("/admin/audit").then((r) => setEvents(r.data.events || []));
  }, []);

  const filtered = useMemo(() => events.filter((e) => {
    if (type !== "all" && e.type !== type) return false;
    if (!q) return true;
    const hay = `${e.user_email || ""} ${e.user_name || ""} ${e.actor_email || ""} ${e.institution || ""} ${e.reason || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [events, q, type]);

  const download = async () => {
    const token = localStorage.getItem("sc_access_token");
    const r = await fetch(`${API}/admin/audit.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "socialcog_audit.csv"; a.click();
  };

  return (
    <div className="space-y-8" data-testid="admin-audit-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-widest text-primary font-semibold">Administración</p>
          <h1 className="heading text-4xl font-extrabold mt-1">Auditoría de decisiones</h1>
          <p className="text-lg text-foreground/70 mt-2 max-w-2xl">
            Registro cronológico de aprobaciones, rechazos, invitaciones creadas y registros por invitación.
            Exportable en CSV para tu comité ético.
          </p>
        </div>
        <Button onClick={download} className="rounded-full h-11 px-5" data-testid="audit-export-btn">
          <Download size={16} className="mr-1" /> Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-border md:col-span-2">
          <CardContent className="p-5">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por usuario, admin, entidad o motivo..."
                className="pl-9 h-10" data-testid="audit-search" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-border">
          <CardContent className="p-5">
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Filtrar</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-10" data-testid="audit-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="approved">Aprobaciones</SelectItem>
                <SelectItem value="rejected">Rechazos</SelectItem>
                <SelectItem value="invitation_created">Invitaciones creadas</SelectItem>
                <SelectItem value="invitation_used">Registros por invitación</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-border">
        <CardHeader>
          <CardTitle className="heading">Eventos ({filtered.length})</CardTitle>
          <CardDescription>Ordenados del más reciente al más antiguo.</CardDescription>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">
              No hay eventos registrados con estos filtros.
            </p>
          ) : (
            <ul className="space-y-3">
              {filtered.map((e, i) => {
                const meta = TYPE_META[e.type] || { label: e.type, Icon: LinkIcon, style: "bg-muted" };
                const Icon = meta.Icon;
                return (
                  <li key={`${e.type}-${e.user_id || e.invitation_id}-${i}`}
                      className="border border-border rounded-xl p-4 flex items-start gap-3" data-testid={`audit-event-${i}`}>
                    <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${meta.style}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="heading font-bold">{meta.label}</span>
                        <span className="text-xs text-muted-foreground">{new Date(e.at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm mt-1 space-y-0.5">
                        {e.user_email && (
                          <p><b>Usuario:</b> {e.user_name} · {e.user_email} {e.user_role && <Badge variant="outline" className="ml-1 capitalize">{e.user_role}</Badge>}</p>
                        )}
                        {e.institution && <p><b>Entidad:</b> {e.institution}</p>}
                        {e.actor_email && <p><b>Admin:</b> {e.actor_email}</p>}
                        {e.reason && <p className="text-destructive"><b>Motivo:</b> {e.reason}</p>}
                        {e.invitation_label && <p><b>Invitación:</b> {e.invitation_label}</p>}
                        {e.type === "invitation_created" && (
                          <p>
                            Rol: <b className="capitalize">{e.allowed_role}</b> ·{" "}
                            {e.auto_approve ? "Acceso directo" : "Requiere revisión"}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
